<script setup>
import { defineEmits, onMounted, ref, computed } from "vue";
import { closestTerms } from "@/repository/api";
import { useBasketStore } from "@/stores/basket";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import AvailableBranches from "@/components/pages/AvailableBranches.vue";
import TermList from "@/components/TermList.vue";
import SelectBranches from "@/components/SelectBranches.vue";
import { useBranchesStore } from "@/stores/branches";
import { initDefaultMerchantFromUrlParams } from "@/utils/merchant";
import { storeToRefs } from "pinia";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const branchStore = useBranchesStore();
const { selectedBranchId, selectedBranchData } = storeToRefs(branchStore);

const basket = useBasketStore();
const router = useRouter();
const userStore = useUserStore();

const emits = defineEmits(["openmodal"]);

const closestTermsData = ref([]);
const termLoading = ref(false);
const isMobile = ref(true);

const openModal = () => {
  emits("openmodal");
};

onMounted(async () => {
  await getTerms();
  await initDefaultMerchantFromUrlParams(userStore);

  const reservationbutton = document.querySelector(".reservation-button");
  const profileshow = document.querySelector(".profile-show");
  if (reservationbutton) {
    reservationbutton.addEventListener("click", (e) => {
      e.preventDefault();
      openModal();
    });
  }

  if (profileshow) {
    profileshow.addEventListener("click", (e) => {
      e.preventDefault();
      openModal();
      if (userStore.loggedIn) {
        router.push({ path: "/my-profile" });
      } else {
        router.push({ path: "/login" });
      }
    });
  }
  isMobile.value = window.innerWidth < 800;
  window.addEventListener("resize", handleResize);
});

const getTerms = async () => {
  termLoading.value = true;
  const response = await closestTerms(selectedBranchId.value);

  if (response) {
    const data = response.terms;
    if (Array.isArray(data)) {
      closestTermsData.value = response.terms.slice(0, 8);
    } else {
      closestTermsData.value = Object.values(response.terms).slice(0, 8);
    }
    termLoading.value = false;
  } else {
    console.log(
      t('errors.failed_to_load_terms_try_again'),
    );
  }
};

const hasSomeTerms = computed(() => {
  return closestTermsData.value.find((term) => {
    return basket.basket.find((item) => item.id === term.id);
  });
});

const proceedReservation = () => {
  router.push({ path: "/reservation" });
  openModal();
};

const handleResize = () => {
  isMobile.value = window.innerWidth < 800;
};
const fullAddressBranch = computed(
  () =>
    `${selectedBranchData.value?.street}, ${selectedBranchData.value?.zip} ${selectedBranchData.value?.city},${selectedBranchData.value?.country}`,
);
</script>

<template>
  <div v-if="selectedBranchData?.title" class="terms-hp">
    <available-branches @changedBranch="getTerms" v-if="!isMobile" />

    <select-branches @changedBranch="getTerms" v-if="isMobile" :in-hp="true" />
    <div v-if="isMobile" class="mobile-branch-data">
      <h3>
        <img
          :src="selectedBranchData?.image_url"
          :alt="selectedBranchData?.title"
        /><span>{{ selectedBranchData?.title }}</span>
      </h3>
      <p>{{ fullAddressBranch }}</p>
    </div>
    <term-list
      :terms="closestTermsData"
      :align-right="true"
      :in-hp="true"
      :loading="termLoading"
    ></term-list>
  </div>
  <div
    class="fusion-separator fusion-full-width-sep"
    style="
      align-self: center;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 20px;
      width: 100%;
    "
  >
    <div
      class="fusion-separator-border sep-single sep-solid"
      style="
        --awb-height: 20px;
        --awb-amount: 20px;
        --awb-sep-color: rgba(0, 0, 0, 0.1);
        border-color: rgba(0, 0, 0, 0.1);
        border-top-width: 1px;
      "
    ></div>
  </div>
  <div style="text-align: right">
    <a
      v-if="hasSomeTerms"
      @click.stop.prevent="proceedReservation"
      class="fusion-button button-flat fusion-button-default-size button-default fusion-button-default button-11 fusion-button-default-span fusion-button-default-type"
      target="_self"
      ><span class="fusion-button-text">{{ $t('reservation.reserve_now') }} </span
      ><i class="fa-angle-right fas button-icon-right" aria-hidden="true"></i
    ></a>
  </div>
  <p style="text-align: right; margin-top: 15px; margin-left: 0px;" class="bottom-text">
    {{ $t('hp.select_terms_or_calendar_message') }}
  </p>
  <div style="text-align: right">
    <a
      id="main-calendar"
      class="fusion-button button-flat fusion-button-default-size button-default fusion-button-default button-12 fusion-button-default-span fusion-button-default-type"
      target="_self"
      @click.prevent.stop="openModal"
    ><span class="fusion-button-text">{{ $t('common.calendar') }}</span
    ><i class="fa-angle-right fas button-icon-right" aria-hidden="true"></i
    ></a>
  </div>

  <Teleport to="body">
    <Transition name="slide-up">
      <div v-if="hasSomeTerms" class="sticky-reserve-bar">
        <a
          @click.stop.prevent="proceedReservation"
          class="fusion-button button-flat fusion-button-default-size button-default fusion-button-default button-11 fusion-button-default-span fusion-button-default-type sticky-reserve-btn"
          target="_self"
        >
          <span class="fusion-button-text">{{ $t('reservation.reserve_now') }}</span>
          <i class="fa-angle-right fas button-icon-right" aria-hidden="true"></i>
        </a>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.terms-hp {
  display: flex;
  justify-content: space-between;
}
.mobile-branch-data {
  display: none;
}
@media screen and (max-width: 1000px) {
  .terms-hp {
    flex-wrap: wrap;
  }
  .bottom-text {
    margin-left: 10px;
    margin-top: 15px;
  }
  #main-calendar {
    margin-left: 0px;
    margin-bottom: 20px;
  }
  .mobile-branch-data {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    h3 {
      display: flex;
      width: 100%;
      margin-bottom: 0;
      font-size: clamp(22px, 5vw, 38px);
      img {
        width: 20%;
        max-width: 50px;
        max-height: 50px;
        border-radius: 0.5rem;
      }
      span {
        display: inline-flex;
        height: 100%;
        padding-left: 10px;
        align-items: center;
        overflow-wrap: break-word;
        min-width: 0;
      }
    }
    p {
      font-weight: bold;
      margin-bottom: 0;
    }
  }
}
</style>

<style lang="scss">
.sticky-reserve-bar {
  display: none;
}

@media screen and (max-width: 1000px) {
  .sticky-reserve-bar {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px 15px calc(12px + env(safe-area-inset-bottom, 0px));
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    text-align: center;
  }

  .sticky-reserve-btn {
    width: 100%;
    display: block;
    text-align: center;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
